import React, { useState, useEffect } from 'react';
import { 
  Layout, Menu, Button, Table, Modal, Upload, Form, Input, message, 
  Typography, Space, Breadcrumb, Dropdown, Popconfirm, Card 
} from 'antd';
import { 
  UploadOutlined, DownloadOutlined, DeleteOutlined, 
  FolderOutlined, FileOutlined, HomeOutlined, 
  UserOutlined, LogoutOutlined, PlusOutlined,
  CloudOutlined, SettingOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const Dashboard = ({ user, onLogout }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [mkdirModalVisible, setMkdirModalVisible] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 获取文件列表
  const fetchFiles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/files', {
        params: { prefix: currentPath },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files);
    } catch (error) {
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [currentPath]);

  // 上传文件
  const handleUpload = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', currentPath);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/files/upload', formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      message.success('文件上传成功');
      fetchFiles();
      setUploadModalVisible(false);
    } catch (error) {
      message.error('文件上传失败');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // 下载文件
  const handleDownload = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/files/download/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success('文件下载成功');
    } catch (error) {
      message.error('文件下载失败');
    }
  };

  // 删除文件
  const handleDelete = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/files/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('文件删除成功');
      fetchFiles();
    } catch (error) {
      message.error('文件删除失败');
    }
  };

  // 创建文件夹
  const handleMkdir = async (values) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/files/mkdir', {
        folderName: `${currentPath}${values.folderName}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('文件夹创建成功');
      fetchFiles();
      setMkdirModalVisible(false);
    } catch (error) {
      message.error('文件夹创建失败');
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.isDir ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined style={{ color: '#1677ff' }} />}
          <Text strong>{record.name.split('/').pop()}</Text>
        </Space>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      render: (size) => size ? `${(size / 1024).toFixed(2)} KB` : '-',
    },
    {
      title: '修改时间',
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {!record.isDir && (
            <Button 
              type="link" 
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.name)}
            >
              下载
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个文件吗？"
            onConfirm={() => handleDelete(record.name)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 生成面包屑导航
  const generateBreadcrumb = () => {
    const paths = currentPath ? currentPath.split('/').filter(Boolean) : [];
    const breadcrumbItems = [
      {
        title: <HomeOutlined />,
        onClick: () => setCurrentPath(''),
      },
    ];

    let current = '';
    paths.forEach((path, index) => {
      current += `${path}/`;
      breadcrumbItems.push({
        title: path,
        onClick: () => setCurrentPath(current),
      });
    });

    return breadcrumbItems;
  };

  // 用户菜单
  const userMenuItems = [
    {
      key: 'user',
      icon: <UserOutlined />,
      label: user?.username || '用户',
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: onLogout,
    },
  ];

  return (
    <Layout className="dashboard-container">
      <Sider width={250} className="sidebar">
        <div className="sidebar-header">
          <Title level={4} style={{ margin: 0, color: '#1677ff' }}>
            <CloudOutlined /> 云盘系统
          </Title>
        </div>
        
        <Menu mode="inline" defaultSelectedKeys={['files']} className="sidebar-menu">
          <Menu.Item key="files" icon={<FolderOutlined />}>
            文件管理
          </Menu.Item>
          <Menu.Item key="shared" icon={<UserOutlined />}>
            共享文件
          </Menu.Item>
          <Menu.Item key="trash" icon={<DeleteOutlined />}>
            回收站
          </Menu.Item>
        </Menu>
        
        <div className="storage-info">
          <Text type="secondary">存储空间</Text>
          <div className="storage-bar">
            <div className="storage-fill" style={{ width: '35%' }}></div>
          </div>
          <div className="storage-text">
            <span>已用 35GB</span>
            <span>总共 100GB</span>
          </div>
        </div>
      </Sider>
      
      <Layout>
        <Header className="header">
          <div className="header-title">文件管理</div>
          <div className="user-info">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />}>
                {user?.username}
              </Button>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="content-container">
          <Card className="file-card">
            <div className="file-header">
              <div>
                <Breadcrumb items={generateBreadcrumb()} />
                <Title level={4} style={{ margin: '8px 0 0 0' }}>文件列表</Title>
              </div>
              <div className="file-actions">
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setMkdirModalVisible(true)}
                >
                  新建文件夹
                </Button>
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalVisible(true)}
                >
                  上传文件
                </Button>
              </div>
            </div>
            
            <Table
              columns={columns}
              dataSource={files.map((file, index) => ({ ...file, key: index }))}
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: '暂无文件' }}
            />
          </Card>
        </Content>
      </Layout>
      
      {/* 上传文件模态框 */}
      <Modal
        title="上传文件"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        className="upload-modal"
      >
        <Upload.Dragger
          name="file"
          multiple={true}
          customRequest={({ file, onSuccess, onError }) => {
            handleUpload(file).then(() => onSuccess()).catch(() => onError());
          }}
          showUploadList={false}
        >
          <div className="upload-area">
            <div className="upload-icon">
              <UploadOutlined />
            </div>
            <div className="upload-text">点击或拖拽文件到此区域上传</div>
            <div className="upload-hint">支持单个或批量上传，单个文件最大100MB</div>
          </div>
        </Upload.Dragger>
        
        {uploading && (
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        )}
      </Modal>
      
      {/* 新建文件夹模态框 */}
      <Modal
        title="新建文件夹"
        open={mkdirModalVisible}
        onCancel={() => setMkdirModalVisible(false)}
        onOk={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true }));
          }
        }}
      >
        <Form onFinish={handleMkdir}>
          <Form.Item
            name="folderName"
            rules={[{ required: true, message: '请输入文件夹名称' }]}
          >
            <input 
              type="text" 
              placeholder="请输入文件夹名称" 
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #d9d9d9',
                borderRadius: '6px'
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Dashboard;